import React, { useState } from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from '../Help/HelpVideoTour';
import { getHelpTour } from '../Help/helpTours/registry';

export const FAB_HIST_GUIDE_ID = 'fab-histogram-help';
const ACCENT = '#0f766e';

function MiniStack({ compact }) {
  const w = compact ? 280 : 360;
  const h = compact ? 118 : 140;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label="Stack versus the capacity line">
      <rect x="0" y="0" width={w} height={h} rx="10" fill="#f8fafc" />
      <text x="12" y="20" fill="#64748b" fontSize="11" fontWeight="700">Man-weeks</text>
      <line x1="36" y1="48" x2={w - 16} y2="48" stroke="#0f172a" strokeWidth="2.5" />
      <text x={w - 14} y="44" fill="#0f172a" fontSize="10" fontWeight="700" textAnchor="end">capacity</text>
      <rect x="48" y="62" width="36" height="40" fill="#1d4ed8" rx="2" />
      <rect x="48" y="46" width="36" height="16" fill="#16a34a" rx="2" />
      <rect x="108" y="70" width="36" height="32" fill="#1d4ed8" rx="2" />
      <rect x="108" y="48" width="36" height="22" fill="#ea580c" rx="2" />
      <rect x="108" y="22" width="36" height="26" fill="#dc2626" rx="2" />
      <rect x="168" y="68" width="36" height="34" fill="#1d4ed8" rx="2" />
      <rect x="168" y="54" width="36" height="14" fill="#9333ea" rx="2" />
      <text x="66" y="116" fill="#64748b" fontSize="10" textAnchor="middle">OK</text>
      <text x="126" y="116" fill="#be123c" fontSize="10" fontWeight="700" textAnchor="middle">too full</text>
      <text x="186" y="116" fill="#64748b" fontSize="10" textAnchor="middle">OK</text>
    </svg>
  );
}

function Card({ icon, title, children, compact }) {
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      padding: compact ? '0.7rem 0.75rem' : '0.85rem 0.9rem',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: '#f0fdfa',
        color: '#0f766e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4, fontSize: compact ? '0.88rem' : '0.95rem' }}>{title}</div>
        <div style={{ color: '#475569', fontSize: compact ? '0.8rem' : '0.86rem', lineHeight: 1.5 }}>{children}</div>
      </div>
    </div>
  );
}

export function FabHistogramHelpBody({ compact = false }) {
  return (
    <div style={{ display: 'grid', gap: compact ? 12 : 16 }}>
      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8, fontSize: compact ? '0.95rem' : '1.05rem' }}>
          Is the shop too full?
        </div>
        <MiniStack compact={compact} />
        <p style={{ margin: '8px 0 0', color: '#475569', fontSize: compact ? '0.82rem' : '0.88rem', lineHeight: 1.5 }}>
          Coloured blocks are leftover hours on each job, shown as whole man-weeks. The dark line is what the shops can take that week.
          Over the line means that week needs more people, or the work has to move.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
        <Card compact={compact} icon="↻" title="Hours changed">
          The page opens on P6. Refresh pulls every remaining resource on the latest dated weekly. Excel dumps are still there: Import reloads the chart when the upload finishes, or Export one file, edit hours, and Import it back. Cost breakdown is not live yet.
        </Card>
        <Card compact={compact} icon="👥" title="Crew or week changed">
          Open Planning. Type men, hours per week, and absences. Save planning. The bars stay; only the line moves.
        </Card>
        <Card compact={compact} icon="▣" title="Need a number on a bar">
          Bar totals sit on top of each stack — on by default, whole numbers. Men on bars is still optional if you want people on each colour.
        </Card>
      </div>
      <Card compact={compact} icon="📅" title="Dates under the bars">
        The chart opens on <strong>From today</strong> — this Sunday and later. Window in the filter drawer switches to All weeks or Through last week, and From / To pinch the range. Weekly grain uses the same three layers as P6 and the Resource Histogram: Sunday day on top, then month, then year.
      </Card>
      <Card compact={compact} icon="✉" title="Weekly email">
        Every Monday at 1:00 PM Eastern, subscribed people get the Weekly Excel (header filters on). Combined peak includes estimates; over-capacity is awarded jobs only. Reply to Project Controls with men, start and finish, estimate number, and job number if awarded — or say what changed on something already on the file.
      </Card>

      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : '1fr 1fr',
        gap: 10,
      }}>
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ fontWeight: 700, color: '#9a3412', marginBottom: 6, fontSize: '0.85rem' }}>Use the file name</div>
          <div style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, color: '#9a3412', marginBottom: 4 }}>Atura 26LG0126.xlsx</div>
          <div style={{ fontSize: 12, color: '#c2410c' }}>not the Project cell (often says the wrong job). “Data Dump” is dropped from the legend.</div>
        </div>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: '0.85rem' }}>Shops on this page</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['East', 'West', 'FL', 'Tank'].map((s) => (
              <span key={s} style={{ background: '#ecfdf5', color: '#0f766e', borderRadius: 999, padding: '3px 8px', fontSize: 12, fontWeight: 600 }}>{s}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.85rem 0.95rem' }}>
      <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: '0.9rem' }}>{title}</div>
      <div style={{ color: '#475569', fontSize: '0.84rem', lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

export function FabHistogramHelpDetails({ loadedLabel, lastImport, workbookCount }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 10, padding: '0.85rem 0.95rem' }}>
        <div style={{ fontWeight: 700, color: '#115e59', marginBottom: 4 }}>This load</div>
        <div style={{ color: '#0f766e', fontSize: '0.84rem', lineHeight: 1.55 }}>
          Loaded {loadedLabel || '—'}.
          {lastImport && lastImport !== '—' ? ` Last dump ${lastImport}.` : ''}
          {workbookCount ? ` ${workbookCount} workbook${workbookCount === 1 ? '' : 's'} in the stack.` : ''}
          {' '}Opens on Primavera P6. Excel dumps (Import / Export) and portal cost breakdown (coming soon) are the other two. They do not mix.
        </div>
      </div>

      <DetailBlock title="What this page is">
        The <strong>Corporation Resource Histogram</strong>: remaining resource load across every P6 working copy — field crews and the fab shops (East, West, FL, Tank) on one stack, with the fab-shop capacity line.
        It grew out of the Pipe Fabrication Consolidated Resource Histogram; filter Resource discipline to Fabrication for that shop-only view.
        Office staffing is on Indirect Resources, not here.
      </DetailBlock>

      <DetailBlock title="How to read the numbers">
        The page opens in <strong>man-weeks</strong> (whole numbers). Units also has <strong>man-months</strong> — man-weeks ÷ weeks in the bucket (average crew), same as the job Resource Histogram — and man-hours.
        The S-curve / right axis is always remaining hours — it does not switch with the bars.
        A whole-number total sits on top of each bar by default (Filters → Bar totals).
        Hover a week to see each job and shop capacity — also whole numbers. A slice that rounds to zero is hidden.
        Group by file name. The Excel Project cell is often wrong (for example Atura labelled as Newterra).
        “Data Dump” is stripped from legend, tooltip, and table names.
        Hover a legend name for the job, estimate, and client — the colour label stays the P6 / file name.
        Shop, Status, Portfolio, Program, Project, and Resource discipline follow each other: pick a program or a project and the other lists only show what those jobs still have.
        Resource discipline starts on Fabrication. Clear it for the corporation curve (a chip beside the title shows the current scope). Two layers above Project: a <strong>program</strong> is jobs (Goodyear); a <strong>portfolio</strong> is programs (Canada). Define them from P6 XERs.
        <br /><br />
        <strong>Legend → Colour by / Also split by</strong> lists the portal keys (file name, Project ID, Resource, Discipline, Shop, …) and then every P6 field the selected copies carry — activity code types, UDFs, and <strong>resource codes such as Resource Standardization</strong> — the same catalog as the EVM report legend. Rows without a value stack as “(no value)”.
      </DetailBlock>

      <DetailBlock title="Why a number looks different from Excel">
        Shop capacity 29.5 now shows as 30. Same rounding as the jobs.
        Say if you want the exact tenth back on capacity only.
        A job at 0.3 man-weeks disappears on hover after rounding. That keeps the card short.
      </DetailBlock>

      <DetailBlock title="Three sources">
        <strong>Primavera P6</strong> — the opening source. <strong>XER copy → Current</strong> is every working copy P6 lists for a job: <code>{'{estimate}'}-Current</code>, <code>-CUR</code>, area files such as <code>-A-Current</code>, and numbered copies (<code>-Current-1</code>, <code>-Current-2</code>) — a job can carry several at once and they all stack. P6 <strong>baselines</strong> stay in P6 (primary and secondary) but are never read as projects here, same as the P6 project list; EPS folders never show either. The Project slicer lists each copy in full — project ID, P6 project name, its project codes (Resp Party, Scope, Status, Calendar, Rev), and P6’s own data date — the same label as the EVM picker, so two copies with the same ID pattern can be told apart; untick any copy to drop it. <strong>Refresh</strong> writes the same remaining hours into Postgres for export / email. Resource discipline is the P6 resource name.
        <br /><br />
        <strong>Excel data dumps</strong> — optional shop files. <strong>Import</strong> one or many files — the chart reloads when the upload finishes. <strong>Export</strong> writes one file with a File name column (download name follows Admin → Settings → Nomenclature, e.g. <code>portal_Fabrication_Histogram_Edit_yyyy-mm-dd.xlsx</code>). Edit hours and Import that same file. There is no separate Reload — Import already refreshes the chart. Excel dumps stay on the Excel source.
        <br /><br />
        <strong>Portal cost breakdown</strong> — coming soon. A bid change will update this chart without a side spreadsheet.
      </DetailBlock>

      <DetailBlock title="Excel import: one file vs one job">
        A one-file export / import (the workbook that has a File name column) <strong>wipes all Excel dumps</strong>, then splits back into jobs using that column. Jobs you delete from the file drop off the chart.
        <br /><br />
        A single-job dump (no File name column) still only replaces that job. Import <strong>Atura 26LG0126.xlsx</strong> again and the old Atura hours are deleted first, then the new file is inserted. You will not see two Atura stacks. A different file name is a new job on the chart.
      </DetailBlock>

      <DetailBlock title="P6 pull">
        The chart reads remaining hours for <strong>every resource</strong> on the XER copies you pick (Current by default) — not only shop / pipe-fab codes. Resource discipline is the P6 resource name, so use that slicer when you only want fabrication. Shop is still guessed from activity / resource text; many field rows land on <strong>Unspecified</strong>.
        <br /><br />
        Remaining hours are spread the way P6’s <strong>Resource Usage Profile</strong> does: remaining units/time × the calendar’s work hours per day (the activity calendar; the resource calendar only for Resource Dependent activities), holidays empty, then rolled to Sunday weeks. A 6x10 crew peaks where P6 peaks. Rows with no readable calendar fall back to an even split.
      </DetailBlock>

      <DetailBlock title="Men, man-weeks, and matching P6">
        <strong>On the P6 source, men come from P6 and nothing else.</strong> Hours are spread on the calendar the activity runs on (the activity calendar; the resource calendar only for Resource Dependent activities), then a bar turns into men by dividing by that calendar’s own <strong>Hours/Week</strong> (5x10 → 50 h, 6x10 → 60 h — the constant in the calendar’s Time Periods, not a holiday-shortened week). That is exactly what P6’s Resource Usage Profile shows with <em>Calculate Average → Base on Hours per Time Period</em> ticked, so the two charts read the same men. No summarize is needed in P6 — the pull reads live remaining units, rates, dates, and calendars.
        <br /><br />
        Planning calendars (named shop → all shops → fallback hours/week) only turn <strong>Excel-dump</strong> hours into men, because those rows carry no calendar. <strong>On the P6 source, absenteeism is off</strong> — it does not change the bars or the capacity line. On an Excel dump it still lowers the capacity line only.
        <br /><br />
        If P6 still shows a different number, check the P6 profile: with <em>Base on Hours per Time Period</em> unticked it divides by the fixed number in the box (50) for every calendar; unticking <em>Calculate Average</em> shows hours, not men.
      </DetailBlock>

      <DetailBlock title="Capacity line">
        On Excel dumps, capacity uses crew × calendar hours per week × (1 − absenteeism) for the fab shops in Planning. On the P6 source that absenteeism factor is left off, so the line is crew × hours per week.
        Hour bars stay planned Remaining Units. Changing Planning moves the dark line; on the Excel source it also moves the people-needed numbers.
        Official shops are East, West, FL, and Tank. Names are editable in Planning and you can add another floor. Hours per week start at 32. Company absenteeism starts at 8 percent. The line is fab-shop crew — turn it off when you look at the whole company.
      </DetailBlock>

      <DetailBlock title="Buttons on the top line">
        Title stays on the left. Filters through PDF sit together on the right.
        Filters, KPIs, and Planning start open. Source, Workbooks, and Period table stay closed until you need them. The page remembers your program, legend, dates, and slicers for your login. A refresh comes back to the same place.
        <br />
        <strong>Filters</strong> sit in a right drawer. Grain (weekly / monthly / quarterly) and Units (man-months / man-weeks / man-hours) are dropdowns, then group, capacity line, S-curve, bar totals, men on bars, hide cancelled, slicers (Shop, Status, Portfolio, Program, Project, Resource discipline). <strong>Add slicer</strong> also lists every P6 column the legend can use — activity codes, UDFs, and resource codes — and checking one filters the bars by that column. A program is a set of projects; a portfolio is a set of programs. Define them from P6 XERs. The slicers cross-filter. Resource discipline defaults to Fabrication; clear it for every trade. Project Clear unchecks every box (empty = no filter). Planning stays as wide as the histogram. Weekly dates under the bars are Year / Month / Sunday day — same as P6 and the Resource Histogram.
        <br />
        <strong>KPIs</strong> — total hours, peak period, weeks over capacity, workbook count.
        <br />
        <strong>Source</strong> — Opens on Primavera P6 (Refresh). Excel dumps (Import / Export) and portal cost breakdown (coming soon) are the other two.
        <br />
        <strong>Planning</strong> — three columns: Crew (shop + men), Calendar (fallback hours/week + date ranges), Absenteeism (default % + month/week rows). Save is on the Planning title row.
        <br />
        <strong>Workbooks</strong> — files in the stack; remove one to drop that job.
        <br />
        <strong>Period table</strong> — the same numbers as the chart, in the units you picked.
        <br />
        <strong>Audit Log</strong> — who imported, exported, deleted a workbook, refreshed P6, or saved Planning / colours. Search and filter Errors / OK. Superadmin can revert Planning and colour changes. Import, Export, delete, and P6 refresh stay in the log but are not one-click revert.
        <br />
        <strong>Weekly email</strong> — every Monday at 1:00 PM Eastern, subscribed people get the Weekly Excel with header filters. Weekly uses Job #, Job name, Estimate #, Estimate name — same as Indirect Resources. Combined peak includes estimates; over-capacity is awarded jobs only. Reply to Project Controls with men, start/finish, estimate #, and job # (if awarded), or say what changed on something already on the file. Turn it on under Admin → Notifications (or Profile → Notification Settings).
      </DetailBlock>

      <DetailBlock title="What comes next">
        Estimation cost breakdowns will feed this same chart so a bid change updates the load without a side spreadsheet.
        The Source button already shows that option. It is not live yet.
        P6 shop mapping, Current-if-newer, and a curve-based remaining spread are still follow-ups.
      </DetailBlock>
    </div>
  );
}

export default function FabricationHistogramHelpModal({
  onClose,
  loadedLabel,
  lastImport,
  workbookCount,
}) {
  const [tab, setTab] = useState('guide');
  const tour = getHelpTour(FAB_HIST_GUIDE_ID, { title: 'Corporation Resource Histogram Help', accent: ACCENT });
  const tabs = [
    { id: 'guide', label: 'Guide' },
    { id: 'details', label: 'Details' },
    ...(tour ? [{ id: 'tour', label: 'Video Tour' }] : []),
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ width: 'min(860px, 100%)', maxHeight: '88vh', background: 'white', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ background: '#0f766e', color: 'white', padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>How to use this page</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={FAB_HIST_GUIDE_ID} title="Corporation Resource Histogram Help" accent={ACCENT} dark />
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexShrink: 0, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                padding: '12px 18px',
                border: 'none',
                flexShrink: 0,
                borderBottom: tab === t.id ? `3px solid ${ACCENT}` : '3px solid transparent',
                backgroundColor: tab === t.id ? 'white' : 'transparent',
                color: tab === t.id ? ACCENT : '#6b7280',
                fontWeight: tab === t.id ? 600 : 400,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ padding: '1rem 1.15rem 1.25rem', overflowY: 'auto' }}>
          {tab === 'guide' && <FabHistogramHelpBody />}
          {tab === 'details' && (
            <FabHistogramHelpDetails
              loadedLabel={loadedLabel}
              lastImport={lastImport}
              workbookCount={workbookCount}
            />
          )}
          {tab === 'tour' && tour && (
            <HelpVideoTour
              guideId={tour.guideId}
              title={tour.title}
              accent={tour.accent}
              scenes={tour.scenes}
              compact
              showHeader
            />
          )}
        </div>
      </div>
    </div>
  );
}
